import { createDisplayNumberFormat } from './displayNumberFormat';
import {toPersianDigits} from './jalali';
import type { TFunction } from 'i18next';
import { formatWeekdayDate } from '../i18n/formatters';
import { formatApiDateCompact } from './calendarPickerLocale';
import type { AppNotification } from '../services/api';

export type WorkoutNotificationKind =
  | 'workout_planned_added'
  | 'workout_planned_updated'
  | 'workout_comment_added'
  | 'workout_record_broken'
  | 'workout_completed'
  | 'workout_ftp_suggested'
  | 'workout_ftp_applied';

/** Group header — Jalali weekday for fa, locale-aware otherwise. */
export function formatNotificationDateLabel(dateKey: string, locale: string): string {
  if (!dateKey) return '';
  const label = formatWeekdayDate(dateKey, locale);
  return /^fa(?:-|$)/i.test(locale) ? toPersianDigits(label) : label;
}

/** Infer workout notification kind from stored description (English or Persian legacy). */
export function inferWorkoutNotificationKind(description?: string | null, storedKind?: string | null): WorkoutNotificationKind | null {
  if (storedKind && ['workout_completed', 'workout_record_broken', 'workout_comment_added', 'workout_planned_added', 'workout_planned_updated', 'workout_ftp_suggested', 'workout_ftp_applied'].includes(storedKind)) return storedKind as WorkoutNotificationKind;
  const text = (description ?? '').trim().toLowerCase();
  if (!text) return null;

  if (text.includes('ftp_suggested')) return 'workout_ftp_suggested';
  if (text.includes('ftp_applied')) return 'workout_ftp_applied';

  if (text.includes('comment') || text.includes('نظر')) {
    return 'workout_comment_added';
  }
  if (text.includes('completed') || text.includes('تکمیل')) {
    return 'workout_completed';
  }
  if (text.includes('added') || text.includes('اضافه')) {
    return 'workout_planned_added';
  }
  if (text.includes('updated') || text.includes('به‌روز') || text.includes('بروز')) {
    return 'workout_planned_updated';
  }
  if (text.includes('record') || text.includes('رکورد')) {
    return 'workout_record_broken';
  }

  return null;
}

function parseTitleFromDescription(description?: string | null): string {
  const trimmed = (description ?? '').trim();
  const colonIdx = trimmed.indexOf(':');
  if (colonIdx >= 0) {
    return trimmed.slice(colonIdx + 1).trim();
  }
  return '';
}

function workoutTitleFromNotifiable(notifiable: Record<string, unknown>): string {
  const title = String(notifiable.title ?? '').trim();
  if (title) return title;
  const type = notifiable.workout_type as { name?: string; english_name?: string } | undefined;
  return String(type?.name ?? type?.english_name ?? '').trim();
}

function formatWorkoutDayLabel(workoutDay: string | undefined, locale: string): string {
  if (!workoutDay) return '';
  return formatApiDateCompact(workoutDay.slice(0, 10), locale);
}

function athleteNameFromNotifiable(notifiable: Record<string, unknown>): string {
  const user = notifiable.user as { full_name?: string; name?: string } | undefined;
  const athlete = notifiable.athlete as { full_name?: string; name?: string } | undefined;
  return String(
    user?.full_name ?? user?.name ?? athlete?.full_name ?? athlete?.name ?? '',
  ).trim();
}

function athleteImageFromNotifiable(notifiable: Record<string, unknown>): string {
  const user = notifiable.user as { profile_image?: string; avatar?: string } | undefined;
  const athlete = notifiable.athlete as { profile_image?: string; avatar?: string } | undefined;
  return String(user?.profile_image ?? user?.avatar ?? athlete?.profile_image ?? athlete?.avatar ?? '').trim();
}

/** Workout owner first; for coach alerts, created_by is the performing athlete. */
function resolveWorkoutAthleteName(
  notifiable: Record<string, unknown>,
  actor: AppNotification['actor'],
  kind: WorkoutNotificationKind,
  isCoachViewer: boolean,
): string {
  const fromOwner = athleteNameFromNotifiable(notifiable);
  if (fromOwner) return fromOwner;

  if (
    isCoachViewer
    && (kind === 'workout_completed' || kind === 'workout_record_broken')
  ) {
    return actor?.full_name?.trim() ?? '';
  }

  return '';
}

export function buildWorkoutNotificationCopy(
  t: TFunction,
  item: AppNotification,
  opts: { isCoachViewer: boolean; locale: string },
) {
  const notifiable = (item.notifiable ?? {}) as Record<string, unknown>;
  const kind = inferWorkoutNotificationKind(item.description, item.workout_kind) ?? 'workout_completed';
  const actorName = item.actor?.full_name?.trim() ?? '';
  const athleteName = resolveWorkoutAthleteName(notifiable, item.actor, kind, opts.isCoachViewer);
  const athleteImage = athleteImageFromNotifiable(notifiable)
    || (opts.isCoachViewer && (kind === 'workout_completed' || kind === 'workout_record_broken')
      ? String(item.actor?.profile_image ?? item.actor?.avatar ?? '').trim()
      : '');
  const workoutTitle =
    workoutTitleFromNotifiable(notifiable) || parseTitleFromDescription(item.description) || t('notifications.workout.fallbackTitle');
  const dateLabel = formatWorkoutDayLabel(String(notifiable.workout_day ?? ''), opts.locale);

  const badgeKey = {
    workout_completed: 'notifications.workout.badgeCompleted',
    workout_planned_added: 'notifications.workout.badgePlanned',
    workout_planned_updated: 'notifications.workout.badgeUpdated',
    workout_comment_added: 'notifications.workout.badgeComment',
    workout_record_broken: 'notifications.workout.badgeRecord',
    workout_ftp_suggested: 'notifications.workout.badgeFtpSuggested',
    workout_ftp_applied: 'notifications.workout.badgeFtpApplied',
  }[kind];

  let messageKey = 'notifications.workout.generic';
  const vars: Record<string, string> = {
    title: workoutTitle,
    date: dateLabel,
    actor: actorName,
    coach: actorName,
    athlete: athleteName,
  };

  if (kind === 'workout_completed') {
    if (opts.isCoachViewer) {
      messageKey = athleteName
        ? 'notifications.workout.coachCompleted'
        : 'notifications.workout.coachCompletedNoAthlete';
    } else if (actorName) {
      messageKey = 'notifications.workout.athleteCompletedCoachUpload';
    } else {
      messageKey = 'notifications.workout.athleteCompletedSelf';
    }
  } else if (kind === 'workout_planned_added') {
    messageKey = actorName
      ? 'notifications.workout.plannedAddedByCoach'
      : 'notifications.workout.plannedAdded';
  } else if (kind === 'workout_planned_updated') {
    messageKey = actorName
      ? 'notifications.workout.plannedUpdatedByCoach'
      : 'notifications.workout.plannedUpdated';
  } else if (kind === 'workout_comment_added') {
    messageKey = actorName
      ? 'notifications.workout.commentByCoach'
      : 'notifications.workout.commentAdded';
  } else if (kind === 'workout_record_broken') {
    if (item.details?.scope === 'year' && Number(item.details.count) > 0) {
      vars.countLabel = createDisplayNumberFormat(opts.locale, { useGrouping: false }).format(Number(item.details.count));
      return { badge: t(badgeKey), message: t(opts.isCoachViewer ? 'annualRecords.coachAchievement' : 'annualRecords.achievement', vars), workoutTitle, athleteName, athleteImage, dateLabel };
    }
    messageKey = opts.isCoachViewer
      ? 'notifications.workout.recordBrokenCoach'
      : 'notifications.workout.recordBrokenAthlete';
  } else if (kind === 'workout_ftp_suggested') {
    messageKey = 'notifications.workout.ftpSuggestedMessage';
  } else if (kind === 'workout_ftp_applied') {
    messageKey = 'notifications.workout.ftpAppliedMessage';
  }

  return {
    badge: t(badgeKey, t('notifications.workout.badge')),
    message: t(messageKey, vars),
    workoutTitle,
    athleteName,
    athleteImage,
    dateLabel,
  };
}

export function notifiableTypeBadge(
  t: TFunction,
  notifiableType?: string,
): string {
  switch (notifiableType) {
    case 'Workout':
      return t('notifications.workout.badge');
    case 'Event':
      return t('notifications.event', 'Event');
    case 'UserMetricData':
      return t('notifications.metric', 'Metric');
    case 'CoachAthleteRequest':
      return t('notifications.coachRequest');
    default:
      return t('notifications.genericUpdate');
  }
}
